import React, { useState, useEffect } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faWarehouse, faTruck, faBox, faClipboardList,
  faCalendarAlt, faCircleInfo, faTimes, faCheck
} from '@fortawesome/free-solid-svg-icons';
import { getShippingRates, displayShippingOptions } from '../services/shippingService';
import '../styles/Cart.css';
import '../styles/modal-shared.css';

function Checkout({ cart, user, onCheckoutComplete, onCancel }) {
  const [shippingDetails, setShippingDetails] = useState({
    name: '', address1: '', address2: '',
    city: '', state: '', zipCode: '', country: 'US',
  });
  const [customerRef,           setCustomerRef]           = useState('');
  const [shippingRates,         setShippingRates]         = useState(null);
  const [loadingRates,          setLoadingRates]          = useState(false);
  const [selectedRate,          setSelectedRate]          = useState(null);
  const [shipEngineAPI,         setShipEngineAPI]         = useState(null);
  const [shippingMethod,        setShippingMethod]        = useState('hold');
  const [holdInstructions,      setHoldInstructions]      = useState('');
  const [rateError,             setRateError]             = useState(null);
  const [showShipEngineWarning, setShowShipEngineWarning] = useState(false);
  const [pendingShipEngine,     setPendingShipEngine]     = useState(false);
  const [fromAddress, setFromAddress] = useState({
    name: '', company_name: '', address_line1: '',
    city_locality: '', state_province: '', postal_code: '', country_code: '', phone: ''
  });
  const [deliveryDate,       setDeliveryDate]       = useState('');
  const [expectedShipDate,   setExpectedShipDate]   = useState('');
  const [validationError,    setValidationError]    = useState('');

  useEffect(() => {
    if (user?.shipEngineAPI) setShipEngineAPI(user.shipEngineAPI);
  }, [user]);

  useEffect(() => {
    if (user?.homeWarehouse) {
      const wh = user.homeWarehouse;
      setFromAddress({
        name:            wh.name || '',
        company_name:    wh.name || '',
        address_line1:   wh.address?.street || '',
        city_locality:   wh.address?.city || '',
        state_province:  wh.address?.state || '',
        postal_code:     wh.address?.zip || '',
        country_code:    'US',
        phone:           wh.address?.phone || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (cart?.length > 0 && cart[0].expDate) setDeliveryDate(cart[0].expDate);
  }, [cart]);

  const calculateTotalWeight = () =>
    Math.max(cart.reduce((s, i) => s + (parseFloat(i.weight) || 0) * (parseInt(i.quantity) || 0), 0), 0.1);

  const fetchShippingRates = async () => {
    if (!shippingDetails?.zipCode) return;
    try {
      setLoadingRates(true);
      setRateError(null);
      if (!fromAddress.address_line1 || !fromAddress.city_locality || !fromAddress.state_province || !fromAddress.postal_code) {
        setRateError('Missing warehouse address information'); return;
      }
      const shippingItems = cart
        .filter(i => (parseInt(i.quantity) || 0) > 0)
        .map(i => ({ weight: parseFloat(i.weight) || 0, quantity: parseInt(i.quantity) || 0, dimLen: parseFloat(i.dimLen) || 12, dimWdt: parseFloat(i.dimWdt) || 8, dimHgt: parseFloat(i.dimHgt) || 6 }));
      if (!shippingItems.length) { setRateError('No valid items in cart'); setShippingRates([]); return; }
      const toAddress = {
        name: shippingDetails.name, phone: shippingDetails.phone || '',
        company_name: shippingDetails.company || '',
        address_line1: shippingDetails.address1, address_line2: shippingDetails.address2 || '',
        city_locality: shippingDetails.city, state_province: shippingDetails.state,
        postal_code: shippingDetails.zipCode, country_code: shippingDetails.country || 'US'
      };
      const rates = await getShippingRates(toAddress, shippingItems, 'shipengine', deliveryDate, fromAddress);
      sessionStorage.setItem('shippingRates', JSON.stringify(rates.rate_response.rates));
      setShippingRates(displayShippingOptions(rates));
    } catch (err) {
      setRateError(err.message || 'Failed to fetch shipping rates');
      setShippingRates([]);
    } finally {
      setLoadingRates(false);
    }
  };

  const handleChange = (e) => setShippingDetails(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError('');
    if (!expectedShipDate) { setValidationError('Please select an expected shipping date'); return; }
    if (shippingMethod === 'shipengine' && !selectedRate) { setValidationError('Please select a shipping option'); return; }
    onCheckoutComplete({
      shippingDetails, customerRef, shippingMethod,
      selectedRate: shippingMethod === 'shipengine' ? selectedRate : null,
      holdInstructions: shippingMethod === 'hold' ? holdInstructions : null,
      expectedShippingDate: expectedShipDate, cart
    });
  };

  const handleShippingMethodChange = (method) => {
    if (method === 'shipengine') { setPendingShipEngine(true); setShowShipEngineWarning(true); }
    else { setShippingMethod(method); setShippingRates(null); setSelectedRate(null); }
  };

  const handleWarningConfirm = () => {
    setShowShipEngineWarning(false);
    setShippingMethod('shipengine');
    setPendingShipEngine(false);
    if (shippingDetails?.zipCode && shippingDetails?.city && shippingDetails?.state) fetchShippingRates();
    else setShippingRates(null);
  };

  const handleWarningCancel = () => {
    setShowShipEngineWarning(false);
    setPendingShipEngine(false);
    setShippingMethod('hold');
  };

  const requiresAddress = (method) => ['internal', 'external', 'shipengine'].includes(method);

  const cartTotals = cart.reduce((a, i) => ({
    units: a.units + (parseInt(i.quantity) || 0),
    weight: a.weight + (parseFloat(i.weight) || 0) * (parseInt(i.quantity) || 0)
  }), { units: 0, weight: 0 });

  const sortedRates = shippingRates ? [...shippingRates].sort((a, b) => parseFloat(a.price) - parseFloat(b.price)) : [];

  return (
    <>
      <div className="checkout-page">
        <div className="checkout-page-inner">
          {/* Header */}
          <div className="checkout-page-header">
            <button className="checkout-back-btn" onClick={onCancel} title="Back to inventory">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h1 className="checkout-page-title">Create Pick Order</h1>
              <p className="checkout-page-subtitle">{cart.length} item{cart.length !== 1 ? 's' : ''} · {cartTotals.units} units · {cartTotals.weight.toFixed(1)} lbs</p>
            </div>
          </div>

          {validationError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: '0.82rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FontAwesomeIcon icon={faCircleInfo} />
              {validationError}
            </div>
          )}

          <Form onSubmit={handleSubmit}>
            <div className="checkout-layout">
              {/* ── LEFT COLUMN ── */}
              <div>
                {/* From warehouse */}
                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faWarehouse} /></div>
                    <h6>Shipping From</h6>
                  </div>
                  <div className="checkout-panel-body">
                    <div className="checkout-warehouse-info">
                      <div className="checkout-warehouse-name">{fromAddress.name || 'Home Warehouse'}</div>
                      <div>{fromAddress.address_line1}</div>
                      <div>{fromAddress.city_locality}, {fromAddress.state_province} {fromAddress.postal_code}</div>
                      {fromAddress.phone && <div>Tel: {fromAddress.phone}</div>}
                    </div>
                  </div>
                </div>

                {/* Ship to */}
                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faTruck} /></div>
                    <h6>Ship To <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.75rem' }}>(Optional)</span></h6>
                  </div>
                  <div className="checkout-panel-body">
                    <div className="row g-2">
                      <div className="col-12">
                        <div className="co-label">Name</div>
                        <Form.Control size="sm" type="text" name="name" value={shippingDetails.name} onChange={handleChange} required={requiresAddress(shippingMethod)} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-12">
                        <div className="co-label">Street Address</div>
                        <Form.Control size="sm" type="text" name="address1" value={shippingDetails.address1} onChange={handleChange} required={requiresAddress(shippingMethod)} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-5">
                        <div className="co-label">City</div>
                        <Form.Control size="sm" type="text" name="city" value={shippingDetails.city} onChange={handleChange} required={requiresAddress(shippingMethod)} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-3">
                        <div className="co-label">State</div>
                        <Form.Control size="sm" type="text" name="state" value={shippingDetails.state} onChange={handleChange} maxLength={2} required={requiresAddress(shippingMethod)} style={{ borderRadius: 8, textTransform: 'uppercase' }} />
                      </div>
                      <div className="col-4">
                        <div className="co-label">Zip</div>
                        <Form.Control size="sm" type="text" name="zipCode" value={shippingDetails.zipCode} onChange={handleChange} maxLength={5} required={requiresAddress(shippingMethod)} style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-6">
                        <div className="co-label">Phone</div>
                        <Form.Control size="sm" type="text" name="phone" value={shippingDetails.phone || ''} onChange={handleChange} style={{ borderRadius: 8 }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reference & date */}
                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                    <h6>Order Details</h6>
                  </div>
                  <div className="checkout-panel-body">
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="co-label">Customer Reference</div>
                        <Form.Control size="sm" type="text" value={customerRef} onChange={(e) => setCustomerRef(e.target.value)} placeholder="Optional" style={{ borderRadius: 8 }} />
                      </div>
                      <div className="col-6">
                        <div className="co-label">Expected Ship Date <span style={{ color: '#ef4444' }}>*</span></div>
                        <Form.Control
                          size="sm"
                          type="date"
                          value={expectedShipDate}
                          onChange={(e) => setExpectedShipDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          required
                          isInvalid={!expectedShipDate}
                          style={{ borderRadius: 8 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping method */}
                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <h6>Shipping Method</h6>
                  </div>
                  <div className="checkout-panel-body">
                    {/* Hold — currently always active */}
                    <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a' }} />
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#15803d' }}>Hold for Instructions</span>
                      </div>
                      <div className="co-label" style={{ marginBottom: 4 }}>Special Instructions (Optional)</div>
                      <textarea
                        className="co-textarea"
                        value={holdInstructions}
                        onChange={(e) => setHoldInstructions(e.target.value)}
                        placeholder="Enter any special handling instructions, delivery notes, or hold conditions…"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── RIGHT COLUMN ── */}
              <div>
                <div className="checkout-panel">
                  <div className="checkout-panel-header">
                    <div className="checkout-panel-icon"><FontAwesomeIcon icon={faBox} /></div>
                    <h6>Order Summary</h6>
                  </div>
                  <div className="checkout-panel-body">
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {cart.map(item => {
                        const ppu = Number(item.ppu) || 1;
                        return (
                          <div key={item.idLevels} className="co-summary-item">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="co-summary-item-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.itemName}</div>
                              <div className="co-summary-item-lot">Lot {item.idLevels} · {item.itemNumber}</div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                              <div className="co-summary-item-qty">{item.quantity} units</div>
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{(item.quantity * ppu).toLocaleString()} pcs</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="co-total-row">
                      <span className="co-total-label">
                        {cart.length} item{cart.length !== 1 ? 's' : ''} · {cartTotals.units} units
                      </span>
                      <span className="co-total-value">{cartTotals.weight.toFixed(1)} lbs</span>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="checkout-footer">
                  <button type="button" className="detail-btn detail-btn--ghost" onClick={onCancel}>
                    <FontAwesomeIcon icon={faTimes} />
                    Cancel
                  </button>
                  <button type="submit" className="detail-btn detail-btn--primary">
                    <FontAwesomeIcon icon={faCheck} />
                    Create Pick Order
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>

      {/* ShipEngine warning modal */}
      <Modal show={showShipEngineWarning} onHide={handleWarningCancel} centered dialogClassName="detail-modal" size="sm">
        <Modal.Header closeButton>
          <div className="modal-title-row">
            <div className="modal-header-icon"><FontAwesomeIcon icon={faCircleInfo} /></div>
            <div className="modal-header-text">
              <h5>External Carrier Notice</h5>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.88rem', lineHeight: 1.6, color: '#475569' }}>
          Your selection indicates your desired service for shipping. Delivery times and rates are estimated
          and may change during order fulfillment. Check your ShipEngine™ dashboard for final charges.
        </Modal.Body>
        <Modal.Footer style={{ gap: 8 }}>
          <button className="detail-btn detail-btn--secondary" onClick={handleWarningCancel}>Cancel</button>
          <button className="detail-btn detail-btn--primary" onClick={handleWarningConfirm}>Continue</button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Checkout;
